import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ShieldCheck, 
  CheckCircle2, 
  FileText, 
  Clock, 
  Phone, 
  ArrowRight,
  MapPin,
  Users,
  Car,
  BadgeCheck,
  Lock,
  Calendar
} from "lucide-react";
import { Link } from "react-router-dom";
import jbsaChapmanAnnex from "@/assets/jbsa-chapman-annex.webp";
import campBullisHero from "@/assets/camp-bullis-hero.avif";

const JBSAMilitaryTransportation = () => {
  const bases = [
    {
      name: "Lackland AFB",
      description: "Home of Air Force Basic Military Training (BMT). We specialize in BMT graduation transportation, ensuring families arrive on time for this momentous occasion.",
      services: ["BMT Graduation pickup/drop-off", "Airman's Week transportation", "Family reunion logistics", "Tech school visits"],
      gate: "Valley Hi Gate (Primary for visitors)"
    },
    {
      name: "Fort Sam Houston",
      description: "Houses Brooke Army Medical Center (BAMC) and numerous military commands. We provide discreet, professional transportation for medical appointments and official business.",
      services: ["BAMC medical appointments", "Command staff transportation", "Official visits", "Warrior Transition Unit support"],
      gate: "Walters Gate (Primary for visitors)"
    },
    {
      name: "Randolph AFB",
      description: "Known as the 'Showplace of the Air Force,' home to Air Education and Training Command. We serve officers, trainers, and flight operations personnel.",
      services: ["Officer training transport", "Conference transportation", "Flight operations personnel", "VIP protocol services"],
      gate: "Main Gate (Pat Booker Road)"
    },
    {
      name: "Camp Bullis",
      description: "JBSA training annex supporting medical and combat training. We provide transportation for military personnel and government contractors accessing this secure facility.",
      services: ["Training personnel transport", "Contractor access", "Medical training support", "Field exercise logistics"],
      gate: "Main Gate (Bullis Road)",
      link: "/camp-bullis-transportation"
    }
  ];

  const credentials = [
    {
      icon: <BadgeCheck className="w-8 h-8" />,
      title: "DBIDS Registered",
      description: "Our chauffeurs are registered in the Defense Biometric Identification System, allowing verified commercial access to all JBSA installations."
    },
    {
      icon: <Car className="w-8 h-8" />,
      title: "Vehicle Inspection Certified",
      description: "All vehicles meet JBSA security requirements and maintain current inspection certifications for base entry."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Background Verified",
      description: "Comprehensive federal background checks ensure our chauffeurs meet military installation security standards."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Commercial Insurance",
      description: "Full commercial liability coverage exceeding military base requirements for passenger transportation services."
    }
  ];

  const passengerRequirements = [
    "Valid government-issued photo ID (Driver's License, State ID, or Passport)",
    "Military ID or CAC card (if applicable)",
    "Sponsor information for base visits (we can assist with coordination)",
    "Full legal name matching your ID for gate access log"
  ];

  const bookingSteps = [
    {
      step: 1,
      title: "Contact Us",
      description: "Call (210) 296-7409 or use our online booking. Mention your JBSA destination."
    },
    {
      step: 2,
      title: "Provide Details",
      description: "Share full names, dates of birth, and ID types for all passengers for access coordination."
    },
    {
      step: 3,
      title: "Confirm Pickup",
      description: "We'll confirm your pickup time, allowing extra time for base security protocols."
    },
    {
      step: 4,
      title: "Seamless Entry",
      description: "Your credentialed chauffeur handles gate procedures while you relax."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "JBSA Military Base Access Car Service",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "description": "Book DBIDS-credentialed chauffeurs for guaranteed JBSA Base Access. Direct, vetted transportation for Lackland BMT graduations, Fort Sam Houston BAMC, and Randolph AFB.",
    "areaServed": {
      "@type": "Place",
      "name": "San Antonio, Texas"
    },
    "serviceType": "Military Base Transportation"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What documentation do I need to travel on base with my chauffeur?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "All passengers 18 years and older must present a valid, REAL ID-compliant photo identification (e.g., driver's license, passport, military ID) to the gate guard. Your chauffeur will handle all the commercial access credentials; we simply require your identification for final clearance."
        }
      },
      {
        "@type": "Question",
        "name": "Can you take me right up to the BMT Review Parade field?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Our chauffeurs know the specific access points and designated drop-off areas for Lackland's BMT graduations. We coordinate with you to ensure a smooth drop-off near the venue entrance, minimizing walking and maximizing your time with your new Airman or Guardian."
        }
      },
      {
        "@type": "Question",
        "name": "Do you serve Camp Bullis?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. We provide transportation to all JBSA installations, including JBSA-Camp Bullis, for training exercises, administrative needs, and official duties. Since access to Camp Bullis is often highly controlled, please book this service with as much advance notice as possible so we can coordinate your specific escort or access requirements."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ricksblackcarservicesantonio.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://ricksblackcarservicesantonio.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "JBSA Military Transportation",
        "item": "https://ricksblackcarservicesantonio.com/jbsa-military-transportation"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Authorized JBSA Base Access Transportation | Lackland, Randolph, Fort Sam Houston"
        description="Authorized JBSA base access transportation. Veteran-owned service providing gate-to-door luxury rides for Lackland, Randolph & Fort Sam Houston."
        canonicalPath="/jbsa-military-transportation"
        keywords="JBSA authorized car service, JBSA base access, Lackland AFB transportation, Fort Sam Houston car service, Randolph AFB shuttle, BMT graduation transportation, DBIDS credentialed driver, military base chauffeur, gate to door military transport"
        schema={[schemaMarkup, faqSchema, breadcrumbSchema]}
      />
      <Navbar />

      {/* Hero Section - The Pain Point */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${campBullisHero})` }}
        />
        {/* Elegant overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/90" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 via-background/30 to-primary/20" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* The Pain Point - Address the problem first */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-full mb-6">
              <Lock className="w-5 h-5 text-red-400" />
              <span className="text-red-300 font-semibold text-sm">The Problem Most Families Face</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              Authorized JBSA Base Access Transportation
              <span className="block text-2xl md:text-3xl text-primary mt-2">Lackland • Randolph • Fort Sam Houston</span>
            </h1>
            
            {/* Pain Point Statement */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 mb-8 max-w-3xl mx-auto">
              <p className="text-lg md:text-xl text-foreground font-medium">
                <span className="text-red-400 font-bold">The Problem:</span> Most rideshare and taxi services <span className="text-red-400 font-bold">cannot enter military bases</span>, leaving you stranded at the gate with your luggage — often miles from your actual destination.
              </p>
            </div>

            {/* The Solution Statement */}
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-8 max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-3">
                <ShieldCheck className="w-6 h-6 text-primary" />
                <span className="text-primary font-bold text-lg">Official Commercial Credentials</span>
              </div>
              <p className="text-lg md:text-xl text-foreground mb-4">
                Unlike other services, Rick's Black Car Service holds <span className="text-primary font-bold">official commercial credentials</span> to enter Lackland AFB, Fort Sam Houston, and Randolph AFB. We provide true <span className="text-primary font-semibold">gate-to-door service</span> in our luxurious 2025 Audi Q7.
              </p>
              <p className="text-muted-foreground">
                <span className="font-semibold text-foreground">The premier choice for BMT Graduation families:</span> We drive you directly to the parade field so you can focus on celebrating your Airman — not navigating base access hassles.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <Button className="btn-luxury text-lg px-8 py-6" asChild>
                <Link to="/book">
                  Book Your Secure Base Transfer
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                <a href="tel:+12102967409">
                  <Phone className="w-5 h-5 mr-2" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section - Specific Scenarios */}
      <section className="py-16 bg-card/50 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Who We Serve</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
              Mission-Critical Transportation
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              From proud families to senior officers, we provide secure, professional transportation for every military need.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* BMT Graduation Families */}
            <Card className="card-luxury border-blue-500/30 bg-card/80 hover:border-blue-400/50 transition-colors">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4">BMT Graduation Families</h3>
                <p className="text-muted-foreground mb-4">
                  Your Airman's graduation is a once-in-a-lifetime moment. We drive you directly to the parade field at Lackland AFB — no gate hassles, no walking miles with grandparents.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    Direct parade field access
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    Airman's Week transportation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    Hotel-to-base shuttles
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* PCS Transfers */}
            <Card className="card-luxury border-green-500/30 bg-card/80 hover:border-green-400/50 transition-colors">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Car className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4">PCS Transfers</h3>
                <p className="text-muted-foreground mb-4">
                  Arriving at your new duty station? We pick you up from SAT Airport and deliver you directly to your on-base housing or temporary lodging facility.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Airport to base housing
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    TLF/IHG Army Hotel access
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Luggage assistance included
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Senior Officer Transport */}
            <Card className="card-luxury border-primary/30 bg-card/80 hover:border-primary/50 transition-colors">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BadgeCheck className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4">Senior Officer Transport</h3>
                <p className="text-muted-foreground mb-4">
                  Discreet, professional service for O-6 and above, visiting dignitaries, and VIP protocol requirements. Confidentiality and punctuality guaranteed.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    VIP protocol trained
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    Multi-base itineraries
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    Command staff support
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vehicle Showcase - Visual Cue for Audi Q7 */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <span className="text-primary text-sm font-semibold tracking-wider uppercase">Your Vehicle</span>
                <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground mb-6">
                  2025 Audi Q7 Black Optic
                </h2>
                <p className="text-muted-foreground mb-6">
                  Our flagship luxury SUV meets every security requirement for JBSA base entry while providing the comfort and professionalism you deserve.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">JBSA Security Compliant</span>
                      <p className="text-sm text-muted-foreground">Meets all vehicle inspection requirements for base entry</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">Quiet Cabin</span>
                      <p className="text-sm text-muted-foreground">Take calls or relax during your transfer</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <span className="font-semibold text-foreground">6-Passenger Capacity</span>
                      <p className="text-sm text-muted-foreground">Perfect for families attending BMT graduations</p>
                    </div>
                  </li>
                </ul>
              </div>
              
              {/* VISUAL CUE: Placeholder for Audi Q7 near base entrance photo */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={jbsaChapmanAnnex} 
                  alt="Rick's Black Car Service 2025 Audi Q7 at JBSA base entrance" 
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-background/90 backdrop-blur-sm rounded-lg p-4 border border-primary/30">
                    <p className="text-sm text-muted-foreground">
                      <span className="text-primary font-semibold">Photo Suggestion:</span> Replace with image of Audi Q7 at JBSA gate entrance for maximum impact
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is JBSA Access? - Educational Section */}
      <section className="py-16 bg-gradient-to-b from-background to-card/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-blue-400 text-sm font-semibold tracking-wider uppercase">Understanding Military Base Transportation</span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
                What is JBSA Access?
              </h2>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Definition */}
              <Card className="card-luxury border-blue-500/30 bg-card/80">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-blue-500/20 rounded-full">
                      <ShieldCheck className="w-8 h-8 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">JBSA Explained</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    <strong className="text-foreground">JBSA</strong> stands for <strong className="text-foreground">Joint Base San Antonio</strong>, a consolidation of three major military installations:
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <span className="text-foreground font-medium">JBSA-Lackland</span>
                      <span className="text-muted-foreground text-sm">(Air Force)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-foreground font-medium">JBSA-Fort Sam Houston</span>
                      <span className="text-muted-foreground text-sm">(Army/Medical)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      <span className="text-foreground font-medium">JBSA-Randolph</span>
                      <span className="text-muted-foreground text-sm">(Air Force)</span>
                    </li>
                  </ul>
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-foreground/90">
                      <strong className="text-blue-400">"JBSA Access"</strong> means the transportation provider (driver and vehicle) has specific security clearances and commercial passes required to drive through security gates and onto the base.
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Right Column - Why It Matters */}
              <Card className="card-luxury border-primary/30 bg-card/80">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-primary/20 rounded-full">
                      <MapPin className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">Why JBSA Access Matters</h3>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <h4 className="font-bold text-red-400 mb-2 flex items-center gap-2">
                        <span>✗</span> Without JBSA Access
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        Uber, Lyft, and standard taxis can only drop you at the Visitor Control Center or main gate. You must exit with luggage, undergo security screening on foot, then walk or wait for a base shuttle—which can be miles to your destination.
                      </p>
                    </div>
                    
                    <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
                      <h4 className="font-bold text-primary mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" /> With Rick's JBSA Access
                      </h4>
                      <p className="text-muted-foreground text-sm">
                        Our credentialed chauffeurs drive through the checkpoint and drop you <strong className="text-foreground">directly at your hotel, barracks, home, or office</strong> inside the base. <span className="text-primary font-semibold">Door-to-door convenience.</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Military City USA Callout */}
            <Card className="card-luxury mt-8 border-primary/20 bg-gradient-to-r from-card via-card/95 to-blue-900/10">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="p-4 bg-primary/20 rounded-full flex-shrink-0">
                    <Users className="w-10 h-10 text-primary" />
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      "Military City USA" — San Antonio's Unique Market
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      San Antonio has one of the <strong className="text-foreground">largest active and retired military populations</strong> in the country. <strong className="text-foreground">Lackland AFB</strong> hosts BMT graduations every week, bringing thousands of families who need reliable transportation. <strong className="text-foreground">Fort Sam Houston</strong> houses SAMMC (San Antonio Military Medical Center), requiring frequent transport for medical professionals and patients. Rick's Black Car Service is uniquely positioned to serve this community with verified base access.
                    </p>
                    <Link to="/blog/what-is-jbsa-access-guide" className="text-primary hover:underline font-semibold inline-flex items-center gap-2">
                      Read Our Complete JBSA Access Guide
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Comparison Table */}
      <section className="py-16 bg-card/50 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Transparent Pricing</span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
                JBSA Base Transportation Rates
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                All-inclusive pricing from SAT Airport. Gratuity included. No hidden fees.
              </p>
            </div>

            {/* Pricing Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 text-foreground font-semibold">Installation</th>
                    <th className="text-center py-4 px-4 text-foreground font-semibold">Distance</th>
                    <th className="text-center py-4 px-4 text-foreground font-semibold">Drive Time</th>
                    <th className="text-center py-4 px-4 text-foreground font-semibold">From SAT Airport</th>
                    <th className="text-center py-4 px-4 text-foreground font-semibold hidden md:table-cell">Primary Gate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/50 hover:bg-card/80 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                        <div>
                          <Link to="/bmt-graduation" className="font-semibold text-foreground hover:text-primary transition-colors">
                            Lackland AFB
                          </Link>
                          <p className="text-xs text-muted-foreground">BMT Graduations</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground">18 miles</td>
                    <td className="text-center py-4 px-4 text-muted-foreground">~25 min</td>
                    <td className="text-center py-4 px-4">
                      <Link to="/quick-quote" className="text-primary font-semibold hover:underline">Get Quote</Link>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground text-sm hidden md:table-cell">Valley Hi Gate</td>
                  </tr>
                  <tr className="border-b border-border/50 hover:bg-card/80 transition-colors bg-primary/5">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        <div>
                          <Link to="/bamc-transportation" className="font-semibold text-foreground hover:text-primary transition-colors">
                            Fort Sam Houston
                          </Link>
                          <p className="text-xs text-muted-foreground">BAMC / Medical</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground">7 miles</td>
                    <td className="text-center py-4 px-4 text-muted-foreground">~12 min</td>
                    <td className="text-center py-4 px-4">
                      <Link to="/quick-quote" className="text-primary font-semibold hover:underline">Get Quote</Link>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground text-sm hidden md:table-cell">Walters Gate</td>
                  </tr>
                  <tr className="border-b border-border/50 hover:bg-card/80 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-primary rounded-full"></div>
                        <div>
                          <Link to="/randolph-afb-transportation" className="font-semibold text-foreground hover:text-primary transition-colors">
                            Randolph AFB
                          </Link>
                          <p className="text-xs text-muted-foreground">AETC / Training</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground">20 miles</td>
                    <td className="text-center py-4 px-4 text-muted-foreground">~28 min</td>
                    <td className="text-center py-4 px-4">
                      <Link to="/quick-quote" className="text-primary font-semibold hover:underline">Get Quote</Link>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground text-sm hidden md:table-cell">Pat Booker Gate</td>
                  </tr>
                  <tr className="hover:bg-card/80 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-amber-400 rounded-full"></div>
                        <div>
                          <Link to="/camp-bullis-transportation" className="font-semibold text-foreground hover:text-primary transition-colors">
                            Camp Bullis
                          </Link>
                          <p className="text-xs text-muted-foreground">Training Annex</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground">19 miles</td>
                    <td className="text-center py-4 px-4 text-muted-foreground">~22 min</td>
                    <td className="text-center py-4 px-4">
                      <Link to="/quick-quote" className="text-primary font-semibold hover:underline">Get Quote</Link>
                    </td>
                    <td className="text-center py-4 px-4 text-muted-foreground text-sm hidden md:table-cell">Main Gate</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Pricing Notes */}
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border">
                <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-foreground text-sm">20% Gratuity Included</span>
                  <p className="text-xs text-muted-foreground">No tipping required at destination</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border">
                <Clock className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-foreground text-sm">Late-Night/Early Morning Surcharge</span>
                  <p className="text-xs text-muted-foreground">+$35 for pickups 9 PM - 5 AM</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-card rounded-lg border border-border">
                <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-foreground text-sm">Additional Stops</span>
                  <p className="text-xs text-muted-foreground">+$25 per stop en route</p>
                </div>
              </div>
            </div>

            {/* Pricing Disclaimer */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border/50">
              <p className="text-sm text-muted-foreground text-center">
                <span className="font-medium text-foreground">Note:</span> Fares shown are estimates from SAT Airport and are calculated dynamically based on distance and time. 
                Actual fare may vary slightly depending on your exact pickup location. Use our{' '}
                <Link to="/request-quote" className="text-primary hover:underline font-medium">Instant Quote Calculator</Link>{' '}
                for a precise quote.
              </p>
            </div>

            <div className="text-center mt-8">
              <Button className="btn-luxury text-lg px-8 py-6" asChild>
                <Link to="/request-quote">
                  Get Your Instant Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="relative max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent z-10" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-primary/10 z-10" />
            <img 
              src={jbsaChapmanAnnex} 
              alt="Joint Base San Antonio Chapman Training Annex entrance sign featuring U.S. Army, Air Force, and Department of Defense emblems"
              className="w-full h-[300px] md:h-[400px] object-cover brightness-110 contrast-105"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-20">
              <p className="text-foreground/90 text-lg md:text-xl font-serif font-medium text-center">
                Verified Commercial Access to All JBSA Installations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lackland AFB BMT Graduation - H2 Feature */}
      <section className="py-20 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">BMT Family Transportation</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
              Dedicated Lackland AFB BMT Graduation Transportation
            </h2>
            <p className="text-muted-foreground mt-4 max-w-3xl mx-auto">
              We specialize in transporting families to Air Force Basic Military Training graduations at Lackland AFB. Our DBIDS-credentialed chauffeurs ensure you arrive on time for this momentous occasion—no gate delays, no stress.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Card className="card-luxury border-2 border-primary/30">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2">
                      <span className="text-2xl">✗</span> Ride-Share Services (Uber/Lyft)
                    </h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        Cannot enter military installations
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        Must drop off at base perimeter
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        No security clearance or vetting
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        Unpredictable service quality
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                      <span className="text-2xl">✓</span> Rick's Black Car Service
                    </h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        Direct access to Lackland AFB parade grounds
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        Door-to-door service on base
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        DBIDS-credentialed chauffeurs
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        Airman's Week transportation available
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Internal Link to BMT Blog */}
                <div className="mt-8 text-center">
                  <Link to="/bmt-graduation" className="text-primary hover:underline font-semibold inline-flex items-center gap-2">
                    Read Our Complete BMT Graduation Transportation Guide
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </CardContent>
            </Card>
            {/* Mid-page CTA */}
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button className="btn-luxury" asChild>
                <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                  Book BMT Graduation Transport
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button variant="outline" className="border-primary/50 hover:bg-primary/10" asChild>
                <a href="tel:+12102967409">
                  <Phone className="w-5 h-5 mr-2" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* DBIDS & Compliance Section - E-E-A-T */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Our Credentials</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
              DBIDS & Commercial Vehicle Compliance: Our Expertise
            </h2>
            <p className="text-muted-foreground mt-4 max-w-3xl mx-auto">
              Gaining commercial vehicle access to JBSA installations requires rigorous compliance with Department of Defense security protocols. Here is exactly how we maintain verified base access:
            </p>
          </div>
          
          {/* E-E-A-T Content - Detailed Process */}
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="card-luxury">
              <CardContent className="p-8 space-y-6">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <BadgeCheck className="w-6 h-6 text-blue-400" />
                      DBIDS Enrollment Process
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Our chauffeurs are enrolled in the <strong className="text-foreground">Defense Biometric Identification System (DBIDS)</strong>—the DoD's mandatory access control system. This requires fingerprint scanning, facial recognition enrollment, and verification against federal databases.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <Car className="w-6 h-6 text-blue-400" />
                      Vehicle Inspection Compliance
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      All vehicles undergo regular inspections meeting JBSA security requirements. We maintain current <strong className="text-foreground">commercial liability insurance</strong> exceeding base minimums and ensure all registration and safety documentation is always current.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <Lock className="w-6 h-6 text-blue-400" />
                      Background Verification
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Federal background checks verify our chauffeurs against criminal databases and watchlists. This vetting process is required for unescorted commercial access and must be renewed periodically.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                      <FileText className="w-6 h-6 text-blue-400" />
                      REAL ID Awareness
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Beginning May 2025, <strong className="text-foreground">REAL ID-compliant identification</strong> will be required for all passengers entering federal facilities. We ensure all passengers are informed of current ID requirements before their trip.
                    </p>
                  </div>
                </div>
                
                {/* Insider Knowledge Callout */}
                <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-300 text-sm">
                    <strong>Important:</strong> The TSA Trusted Traveler programs (TSA PreCheck, Global Entry) are <em>not</em> valid for commercial vehicle base access. DBIDS registration is the only approved method for commercial transportation providers entering JBSA installations.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {credentials.map((cred, index) => (
              <Card key={index} className="card-luxury text-center">
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-center text-blue-400">{cred.icon}</div>
                  <h3 className="text-lg font-semibold text-foreground">{cred.title}</h3>
                  <p className="text-muted-foreground text-sm">{cred.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fort Sam Houston & BAMC Section */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Medical & Command Transportation</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
              Fort Sam Houston & BAMC Medical Appointment Transport
            </h2>
            <p className="text-muted-foreground mt-4 max-w-3xl mx-auto">
              Brooke Army Medical Center (BAMC) at Fort Sam Houston is one of the nation's premier military medical facilities. We provide discreet, professional transportation for medical appointments, ensuring patients arrive relaxed and on time.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {bases.map((base, index) => (
              <Card key={index} className="card-luxury h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-8 h-8 text-blue-400" />
                    <h3 className="text-xl font-bold text-foreground">{base.name}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm">{base.description}</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      {base.gate}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-2">Services:</p>
                    <ul className="space-y-1">
                      {base.services.map((service, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-primary flex-shrink-0" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Internal Link to BAMC */}
          <div className="mt-8 text-center">
            <Link to="/bamc-transportation" className="text-primary hover:underline font-semibold inline-flex items-center gap-2">
              Learn More About BAMC Medical Appointment Transportation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {/* Mid-page CTA */}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button className="btn-luxury" asChild>
              <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                Book Medical Transport
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button variant="outline" className="border-primary/50 hover:bg-primary/10" asChild>
              <a href="tel:+12102967409">
                <Phone className="w-5 h-5 mr-2" />
                (210) 296-7409
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Randolph AFB Section */}
      <section className="py-20 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Executive Transportation</span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
                Randolph AFB & Executive Officer Transport
              </h2>
              <p className="text-muted-foreground mt-4 max-w-3xl mx-auto">
                Known as the "Showplace of the Air Force," Randolph AFB is home to Air Education and Training Command headquarters. We provide executive-level transportation for officers, VIP visitors, and official protocol services.
              </p>
            </div>
            <Card className="card-luxury">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">Executive Services</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Officer training transport
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Conference and event transportation
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        VIP protocol services
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary" />
                        Flight operations personnel
                      </li>
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">Passenger Requirements</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      {passengerRequirements.map((req, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-blue-300 text-sm flex items-start gap-2">
                    <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>Privacy Assurance:</strong> All passenger information is handled with strict confidentiality and used only for JBSA base access coordination. We comply with all military privacy protocols.
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
            {/* Internal Link to Randolph */}
            <div className="mt-8 text-center">
              <Link to="/randolph-afb-transportation" className="text-primary hover:underline font-semibold inline-flex items-center gap-2">
                View Randolph AFB Transportation Details & Pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - H2 */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
              JBSA Transportation FAQs & Booking Guide
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Everything you need to know about booking transportation to Joint Base San Antonio installations.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {/* FAQ 1 */}
            <Card className="card-luxury">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-start gap-3">
                  <span className="text-primary font-serif text-xl">Q:</span>
                  What documentation do I need to travel on base with my chauffeur?
                </h3>
                <p className="text-muted-foreground pl-8">
                  All passengers 18 years and older must present a valid, <strong className="text-foreground">REAL ID-compliant photo identification</strong> (e.g., driver's license, passport, military ID) to the gate guard. Your chauffeur will handle all the commercial access credentials; we simply require your identification for final clearance.
                </p>
              </CardContent>
            </Card>
            
            {/* FAQ 2 */}
            <Card className="card-luxury">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-start gap-3">
                  <span className="text-primary font-serif text-xl">Q:</span>
                  Can you take me right up to the BMT Review Parade field?
                </h3>
                <p className="text-muted-foreground pl-8">
                  <strong className="text-foreground">Yes.</strong> Our chauffeurs know the specific access points and designated drop-off areas for Lackland's BMT graduations. We coordinate with you to ensure a smooth drop-off near the venue entrance, minimizing walking and maximizing your time with your new Airman or Guardian.
                </p>
              </CardContent>
            </Card>
            
            {/* FAQ 3 */}
            <Card className="card-luxury">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-foreground mb-3 flex items-start gap-3">
                  <span className="text-primary font-serif text-xl">Q:</span>
                  Do you serve Camp Bullis?
                </h3>
                <p className="text-muted-foreground pl-8">
                  <strong className="text-foreground">Absolutely.</strong> We provide transportation to all JBSA installations, including JBSA-Camp Bullis, for training exercises, administrative needs, and official duties. Since access to Camp Bullis is often highly controlled, please book this service with as much advance notice as possible so we can coordinate your specific escort or access requirements.
                </p>
              </CardContent>
            </Card>
          </div>
          
          {/* Booking Steps */}
          <div className="mt-16">
            <h3 className="text-2xl font-serif font-bold text-center text-foreground mb-8">How to Book Your JBSA Transportation</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {bookingSteps.map((item, index) => (
                <Card key={index} className="card-luxury text-center relative">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-2xl font-bold text-primary">{item.step}</span>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground">{item.title}</h4>
                    <p className="text-muted-foreground text-sm">{item.description}</p>
                  </CardContent>
                  {index < bookingSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-primary/50" />
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <ShieldCheck className="w-16 h-16 text-blue-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
              Ready for Guaranteed JBSA Base Access?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Don't risk being turned away at the gate. Book with San Antonio's only verified JBSA access car service for transportation to Lackland AFB, Fort Sam Houston, Randolph AFB, and Camp Bullis.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button className="btn-luxury text-lg px-8 py-6" asChild>
                <a href="http://ricksblackcarservicesantonio.square.site" target="_blank" rel="noopener noreferrer">
                  Book JBSA Access Car Service Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                <a href="tel:+12102967409">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Resource Section */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto p-8 bg-primary/5 border border-primary/20 rounded-xl text-center">
            <h3 className="text-xl font-semibold mb-3">Complete JBSA Transportation Guide</h3>
            <p className="text-muted-foreground mb-4">
              Read our comprehensive blog post covering everything about JBSA military base access transportation—credentials, requirements, pricing, and booking tips.
            </p>
            <Button asChild className="btn-luxury">
              <Link to="/blog/jbsa-military-base-access-transportation-guide" className="inline-flex items-center gap-2">
                Read the Full Guide
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Related Military Services */}
      <section className="py-20 bg-card/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Related Services</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mt-2 text-foreground">
              More Military Transportation Services
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Card className="card-luxury">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-foreground mb-2">BMT Graduation</h3>
                <p className="text-muted-foreground text-sm mb-4">Transportation for Air Force Basic Military Training graduation ceremonies at Lackland AFB.</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/bmt-graduation">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="card-luxury">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-foreground mb-2">BAMC Transportation</h3>
                <p className="text-muted-foreground text-sm mb-4">Professional medical appointment transportation to Brooke Army Medical Center at Fort Sam Houston.</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/bamc-transportation">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="card-luxury">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-foreground mb-2">Camp Bullis</h3>
                <p className="text-muted-foreground text-sm mb-4">Reliable transportation to San Antonio's military training installation for personnel and contractors.</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/camp-bullis-transportation">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="card-luxury">
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-semibold text-foreground mb-2">Military Families Hub</h3>
                <p className="text-muted-foreground text-sm mb-4">Complete transportation solutions for military families stationed at JBSA installations.</p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/military-families">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Instant Quote CTA */}
      <InstantQuoteCTA />

      <Footer />
    </div>
  );
};

export default JBSAMilitaryTransportation;
